import React from 'react'
import styled from 'styled-components'
import tw from 'twin.macro'
import PopLink from '@components/PopLink'
import { useAmp } from 'next/amp'
import {
  RecommendedArticles_recommendedArticles_articles_publisher,
  RecommendedArticles_recommendedArticles_articles_categories,
} from '@apollo-types/RecommendedArticles'
import { DesktopDisplayContainer } from '@components/DeviceContainer'
import PopImage from '@components/PopImage'
import { publicUrl } from '@libs/appUrl'

interface DesktopPopularArticleImage {
  url: string
  noShaper: boolean
}
interface DesktopPopularArticleCardProps {
  completedUrl: string
  title: string
  displayedPlainTextContent: string
  isApp: boolean
  publisher: RecommendedArticles_recommendedArticles_articles_publisher
  articleImage: DesktopPopularArticleImage
  trackEnterEvent: () => void
  categories: (RecommendedArticles_recommendedArticles_articles_categories | null)[] | null
}

function DesktopPopularArticleCard(props: DesktopPopularArticleCardProps) {
  const {
    completedUrl,
    title,
    displayedPlainTextContent,
    publisher,
    isApp,
    articleImage,
    trackEnterEvent,
    categories,
  } = props

  const isAmp = useAmp()
  const Img = isAmp ? 'amp-img' : 'img'
  const isSecret = categories?.map((category) => category.name)?.includes('匿名聊')

  return (
    <DesktopDisplayContainer>
      <Container>
        <TitleAndPublisherContainer>
          <PopLink url={completedUrl} shouldOpenInCurrentTab onClick={trackEnterEvent}>
            <Title>{title || displayedPlainTextContent}</Title>
          </PopLink>
          <PublisherContainer>
            <PopLink
              url={publicUrl(`/user/${publisher?.mid}`)}
              shouldOpenInCurrentTab={!isApp}
              disableOnClick={isSecret}
            >
              <div className="photo-container">
                {publisher?.photoUrl && <Img src={publisher.photoUrl} width="20" height="20" />}
              </div>
              <p className="user-name">{publisher?.name}</p>
            </PopLink>
          </PublisherContainer>
        </TitleAndPublisherContainer>
        <ArticleImageContainer>
          <PopLink url={completedUrl} shouldOpenInCurrentTab onClick={trackEnterEvent}>
            {!isAmp ? (
              <ArticleImage>
                <PopImage
                  noShaper={articleImage.noShaper}
                  imageUrl={articleImage.url}
                  sizes="80px"
                  ratio={1}
                />
              </ArticleImage>
            ) : (
              <amp-img src={articleImage.url} width="100" height="100" layout="responsive" />
            )}
          </PopLink>
        </ArticleImageContainer>
      </Container>
    </DesktopDisplayContainer>
  )
}

const Container = styled.div`
  ${tw`flex w-full mb-1 rounded-sm`}
  ${tw`cursor-pointer transition duration-500`}
  ${tw`p-2`}
  padding: 8px;
  :hover {
    background-color: #f3f3ff;
  }
`

const TitleAndPublisherContainer = styled.div`
  ${tw`flex-1`}
`

const Title = styled.h2`
  ${tw`text-h5 mb-4 font-medium`}
  color: black;
  height: 44px;
  width: 196px;
  line-height: 1.35;
  /* the workaround for text-overflow: ellipsis; only works in 1 line text */
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const PublisherContainer = styled.div`
  a {
    ${tw`flex`}
  }
  img {
    ${tw`rounded-full`}
    width: 20px;
    height: 20px;
  }
  .photo-container {
    ${tw`mr-2`}
  }
  .user-name {
    ${tw`text-h6-d`}
    color: black;
  }
`

const ArticleImageContainer = styled.div`
  ${tw`ml-2`}
  img {
    ${tw`rounded-sm object-cover`}
  }
`

const ArticleImage = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  img {
    ${tw`w-full h-full`}
  }
`

export default DesktopPopularArticleCard
